import { OmniScrollArea } from "@/components/artifacts/OmniScrollArea.tsx";
import { OmniPickerRow } from "@/components/artifacts/omni/OmniPickerRow.tsx";
import { useFlexibleMeetings } from "@/components/scheduling/useFlexibleMeetings.ts";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { describeRecurrence } from "@/shared/recurrenceLabel.ts";
import { Lock } from "lucide-react";

/**
 * The state words for a meeting row, shared with the settings screen (which
 * the canvas card renders) so all three surfaces speak identically: checked
 * rows say "Flexible" in the answer color, unchecked rows say "Protected"
 * behind the lock.
 */
export const MeetingFlexQualifier = ({ isMovable }: { isMovable: boolean }) =>
  isMovable ? (
    <span className="font-medium text-primary">Flexible</span>
  ) : (
    <span className="flex items-center gap-1">
      <Lock className="size-3" aria-hidden />
      Protected
    </span>
  );

/**
 * The movable-meetings chooser, sized for the omnibox card.
 *
 * Same question and the same `useFlexibleMeetings` state as the canvas card, in
 * a denser row. The canvas has a screen height to spend and can afford the
 * settings layout; here the card floats over the thread above a "Done" row and
 * a follow-up box, so the list is capped and scrolls rather than pushing the
 * composer off the top of the viewport.
 *
 * Every series the query returns gets a row. Guessing which ones "look movable"
 * would hide the exact options the user came here to weigh.
 *
 * Picks save as they are made — the same autosave every other surface uses — so
 * "Done" ends the step rather than committing anything, and a user who dismisses
 * the card keeps whatever they had already marked.
 */
export const ProtectedMeetingsOmniPickerBody = () => {
  const { series, isLoading, isSaving, canMove, toggle } = useFlexibleMeetings();

  // Skeleton rows rather than a spinner: they hold the height the list is about
  // to take, so the card doesn't grow under the cursor as the meetings land.
  // (A spinner here would also drag the lottie player into every biscuit's
  // import graph — this body is reached from the shared card chrome.)
  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 rounded-md border p-3">
        <Skeleton className="h-3.5 w-40" />
        <Skeleton className="h-3.5 w-28" />
        <Skeleton className="h-3.5 w-36" />
      </div>
    );
  }

  // `pickProtectedMeetings` puts up no card when there is nothing recurring, so
  // this is the series list emptying under a card already on screen — a meeting
  // series ending between the card going up and the user reaching it. Say so,
  // because a card with an empty middle reads as a card that failed to load.
  if (series.length === 0) {
    return (
      <p className="rounded-md border p-3 text-sm text-muted-foreground">
        No recurring meetings on your calendars right now.
      </p>
    );
  }

  return (
    <div className="overflow-hidden rounded-md border">
      {/* Whole rows, never a sliced one. These rows carry a sublabel, so each is
          52px rather than the 36px the other pickers use: three on a phone, where
          this card shares the screen with the keyboard, and four on a desktop.
          The card lands at much the same height as its siblings even though it
          holds fewer rows. */}
      <OmniScrollArea className="max-h-[156px] md:max-h-[208px]">
        {series.map((s) => {
          const isMovable = canMove(s.externalRecurringEventId);
          return (
            <OmniPickerRow
              key={s.externalRecurringEventId}
              checked={isMovable}
              onToggle={() => void toggle(s.externalRecurringEventId)}
              disabled={isSaving}
              label={s.subject}
              sublabel={[describeRecurrence(s), s.location].filter(Boolean).join(" · ")}
              // Both states are labelled: the words carry the polarity a bare
              // check can only imply — checked means "this one could move",
              // the inverse of the calendars card beside it.
              qualifier={<MeetingFlexQualifier isMovable={isMovable} />}
              ariaState={isMovable ? "Flexible" : "Protected"}
              // Protected is the resting state, not "off" — an unchecked row
              // is not dimmer than a checked one.
              dimUnchecked={false}
              statsigName="omni-picker-protected-meeting-toggle"
              className="border-b last:border-b-0"
            />
          );
        })}
      </OmniScrollArea>
    </div>
  );
};
